
import React, { useEffect, Suspense, useState } from 'react';
import { HashRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { TerminologyProvider } from './context/TerminologyContext'; 
import { PublicLayout } from './components/Layout';
import { Loader2 } from 'lucide-react';
import { SEO } from './components/SEO';
import { FeatureGuard } from './components/auth/FeatureGuard'; 
import { getAppMode } from './utils/config'; 
import { getBrandingSettings, getStudioByOwner } from './services/dataService';

const FaviconInjector = () => {
    const { tenant } = useTenant();
    
    useEffect(() => {
        const fetchFavicon = async () => {
            try {
                let faviconUrl = '';
                if (tenant?.ownerId) {
                    const studio = await getStudioByOwner(tenant.ownerId);
                    if (studio?.faviconUrl) faviconUrl = studio.faviconUrl;
                    else if (studio?.logoUrl) faviconUrl = studio.logoUrl;
                }
                if (!faviconUrl) {
                    const b = await getBrandingSettings();
                    faviconUrl = b?.faviconUrl || b?.logoUrl || '';
                }

                if (faviconUrl) {
                    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
                    if (!link) {
                        link = document.createElement('link');
                        link.rel = 'icon';
                        document.getElementsByTagName('head')[0].appendChild(link);
                    }
                    link.href = faviconUrl;
                }
            } catch (e) {
                console.error("Failed to inject favicon", e);
            }
        };
        fetchFavicon();
    }, [tenant]);
    return null;
};

// --- Lazy Load Pages ---
const ProtectedRoute = React.lazy(() => import('./modules/auth/components/ProtectedRoute').then(module => ({ default: module.ProtectedRoute })));

// Auth
const Login = React.lazy(() => import('./modules/auth/pages/Login').then(m => ({ default: m.Login })));
const StaffLogin = React.lazy(() => import('./modules/auth/pages/StaffLogin').then(m => ({ default: m.StaffLogin })));
const GoogleCallback = React.lazy(() => import('./modules/auth/pages/GoogleCallback').then(m => ({ default: m.GoogleCallback })));
const SSOCallback = React.lazy(() => import('./modules/auth/pages/SSOCallback').then(m => ({ default: m.SSOCallback })));

// Admin
const SuperAdminDashboard = React.lazy(() => import('./modules/saas/pages/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const SaaSLanding = React.lazy(() => import('./modules/saas/pages/SaaSLanding').then(m => ({ default: m.SaaSLanding })));
const SaaSRegister = React.lazy(() => import('./modules/saas/pages/SaaSRegister').then(m => ({ default: m.SaaSRegister })));

// Public
const StudioLanding = React.lazy(() => import('./modules/core/pages/StudioLanding').then(m => ({ default: m.StudioLanding })));
const About = React.lazy(() => import('./modules/core/pages/About').then(m => ({ default: m.About }))); 
const ServicesPage = React.lazy(() => import('./modules/services/pages/PublicServices').then(m => ({ default: m.ServicesPage })));
const WorkshopPage = React.lazy(() => import('./modules/workshop/pages/WorkshopPage').then(m => ({ default: m.WorkshopPage })));
const Contact = React.lazy(() => import('./modules/contact/pages/Contact').then(m => ({ default: m.Contact })));
const Portfolio = React.lazy(() => import('./modules/portfolio/pages/PortfolioGrid').then(m => ({ default: m.Portfolio })));
const PortfolioDetail = React.lazy(() => import('./modules/portfolio/pages/PortfolioDetail').then(m => ({ default: m.PortfolioDetail })));
const PublicCV = React.lazy(() => import('./modules/portfolio/pages/PublicCV').then(m => ({ default: m.PublicCV })));
const Marketplace = React.lazy(() => import('./modules/marketplace/pages/Marketplace').then(m => ({ default: m.Marketplace })));
const NotFound = React.lazy(() => import('./modules/core/pages/NotFound').then(m => ({ default: m.NotFound })));

// Legal
const Privacy = React.lazy(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.Privacy })));
const Terms = React.lazy(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.Terms })));
const RefundPolicy = React.lazy(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.RefundPolicy })));
const EULA = React.lazy(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.EULA })));

// Booking Engines
const QuoteRequest = React.lazy(() => import('./modules/orders/pages/QuoteRequest').then(m => ({ default: m.QuoteRequest })));
const ServiceBooking = React.lazy(() => import('./modules/bookings/pages/BookingEngine').then(m => ({ default: m.ServiceBooking })));
const ServiceOrder = React.lazy(() => import('./modules/orders/pages/ServiceOrder').then(m => ({ default: m.ServiceOrder })));

// Dashboard
const Overview = React.lazy(() => import('./modules/core/pages/Overview').then(m => ({ default: m.Overview })));
const Bookings = React.lazy(() => import('./modules/bookings/pages/BookingsList').then(m => ({ default: m.Bookings })));
const Schedule = React.lazy(() => import('./modules/bookings/pages/Availability').then(m => ({ default: m.Schedule })));
const Orders = React.lazy(() => import('./modules/orders/pages/Orders').then(m => ({ default: m.Orders })));
const Files = React.lazy(() => import('./modules/core/pages/Files').then(m => ({ default: m.Files })));
const Clients = React.lazy(() => import('./modules/core/pages/Clients').then(m => ({ default: m.Clients })));
const Team = React.lazy(() => import('./modules/core/pages/Team').then(m => ({ default: m.Team })));
const Settings = React.lazy(() => import('./modules/core/pages/Settings').then(m => ({ default: m.Settings })));
const ClientServices = React.lazy(() => import('./modules/services/pages/ServiceCatalog').then(m => ({ default: m.ClientServices })));
const ServicesManager = React.lazy(() => import('./modules/services/pages/ServicesManager').then(m => ({ default: m.ServicesManager })));
const WorkshopManager = React.lazy(() => import('./modules/workshop/pages/WorkshopManager').then(m => ({ default: m.WorkshopManager })));
const Finances = React.lazy(() => import('./modules/finance/pages/Finances').then(m => ({ default: m.Finances })));
const ContractTemplates = React.lazy(() => import('./modules/legal/pages/ContractTemplates').then(m => ({ default: m.ContractTemplates })));
const StudioSettings = React.lazy(() => import('./modules/studio/pages/StudioSettings').then(m => ({ default: m.StudioSettings })));
const CVEditor = React.lazy(() => import('./modules/portfolio/pages/CVEditor').then(m => ({ default: m.CVEditor })));
const Newsletters = React.lazy(() => import('./modules/marketing/pages/Newsletters').then(m => ({ default: m.Newsletters })));
const FormManager = React.lazy(() => import('./modules/forms/pages/FormManager').then(m => ({ default: m.FormManager })));
const ClientMessages = React.lazy(() => import('./modules/contact/pages/ClientMessages').then(m => ({ default: m.ClientMessages })));

const PageLoader = () => (
  <div className="h-screen flex items-center justify-center bg-black text-neutral-500 font-mono text-xs">
    <Loader2 className="animate-spin mr-2" /> LOADING...
  </div>
);

// Root Router to handle Domain Splitting
const RootRoute = () => {
  const mode = getAppMode();

  // 1. SaaS Platform Mode (Marketing Site)
  if (mode === 'MARKETPLACE') {
      return <SaaSLanding />;
  }

  // 2. Portal Mode (Gateway - c.janoobistudios.app)
  // Shows generic login, no studio data. Redirects to login immediately.
  if (mode === 'PORTAL') {
      return <Navigate to="/login" replace />;
  }

  // 3. Studio Mode (Tenant/Custom - neon.c.janoobistudios.app)
  // Note: StudioLanding page handles dynamic SEO injection based on studio settings
  return (
    <PublicLayout>
      <div className="relative">
        <StudioLanding />
      </div>
    </PublicLayout>
  );
};

const DashboardRoutes = () => (
    <>
        <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
        
        {/* Gated Modules */}
        <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
        <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
        <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
        <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
        <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
        <Route path="requests" element={<ProtectedRoute><ClientMessages /></ProtectedRoute>} />
        <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
        <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
        <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
        <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
        <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
        <Route path="studio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><StudioSettings /></ProtectedRoute>} />
        <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
        <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
        <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
    </>
);

const PublicRoutes = () => (
    <>
        <Route path="about" element={<PublicLayout><About /></PublicLayout>} />
        <Route path="services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
        <Route path="studio/:studioId" element={<PublicLayout><ServicesPage /></PublicLayout>} />
        {/* NEW: Custom Slug Route for Artists/Owners */}
        <Route path="owner/:slug" element={<PublicLayout><ServicesPage /></PublicLayout>} />
        
        <Route path="workshops" element={<PublicLayout><WorkshopPage /></PublicLayout>} />
        <Route path="portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
        <Route path="portfolio/:id" element={<PublicLayout><PortfolioDetail /></PublicLayout>} />
        <Route path="cv" element={<PublicLayout><PublicCV /></PublicLayout>} />
        <Route path="cv/:id" element={<PublicLayout><PublicCV /></PublicLayout>} />
        <Route path="contact" element={<PublicLayout><Contact /></PublicLayout>} />
        
        {/* Legal */}
        <Route path="privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
        <Route path="terms" element={<PublicLayout><Terms /></PublicLayout>} />
        <Route path="refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
        <Route path="eula" element={<PublicLayout><EULA /></PublicLayout>} />

        {/* Engines */}
        <Route path="quote/:serviceId" element={<PublicLayout><QuoteRequest /></PublicLayout>} />
        <Route path="book/:id" element={<PublicLayout><ServiceBooking /></PublicLayout>} />
        <Route path="order/:id" element={<PublicLayout><ServiceOrder /></PublicLayout>} />
        
        {/* Auth */}
        <Route path="login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />
    </>
);

const AppRoutes = () => {
    return (
        <Routes>
            {/* 1. Global / Subdomain Routes */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/marketplace" element={<PublicLayout><SEO title="Marketplace" /><Marketplace /></PublicLayout>} />
            <Route path="/saas-landing" element={<SaaSLanding />} />
            
            <Route path="/admin" element={<StaffLogin />} />
            <Route path="/google-callback" element={<GoogleCallback />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            
            {/* SaaS Admin */}
            <Route path="/saas-admin" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN']}><SuperAdminDashboard /></ProtectedRoute>} />
            
            {/* Shared Public Routes (Root based) */}
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/studio/:studioId" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/owner/:slug" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            
            <Route path="/workshops" element={<PublicLayout><WorkshopPage /></PublicLayout>} />
            <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
            <Route path="/portfolio/:id" element={<PublicLayout><PortfolioDetail /></PublicLayout>} />
            <Route path="/cv" element={<PublicLayout><PublicCV /></PublicLayout>} />
            <Route path="/cv/:id" element={<PublicLayout><PublicCV /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
            <Route path="/refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
            <Route path="/eula" element={<PublicLayout><EULA /></PublicLayout>} />
            <Route path="/quote/:serviceId" element={<PublicLayout><QuoteRequest /></PublicLayout>} />
            <Route path="/book/:id" element={<PublicLayout><ServiceBooking /></PublicLayout>} />
            <Route path="/order/:id" element={<PublicLayout><ServiceOrder /></PublicLayout>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />

            {/* Dashboard (Root based) */}
            <Route path="/dashboard">
                <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
                <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
                <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
                <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
                <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
                <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
                <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
                <Route path="requests" element={<ProtectedRoute><ClientMessages /></ProtectedRoute>} />
                <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
                <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
                <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
                <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
                <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
                <Route path="studio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><StudioSettings /></ProtectedRoute>} />
                <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
                <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
                <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
            </Route>

            {/* 2. Path-Based Tenant Routes ( /t/:tenantSlug/... ) */}
            <Route path="/t/:tenantSlug">
                <Route index element={<PublicLayout><StudioLanding /></PublicLayout>} />
                
                {/* Replicate Public Routes under /t/:tenantSlug */}
                <Route path="about" element={<PublicLayout><About /></PublicLayout>} />
                <Route path="services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
                <Route path="owner/:slug" element={<PublicLayout><ServicesPage /></PublicLayout>} />
                <Route path="workshops" element={<PublicLayout><WorkshopPage /></PublicLayout>} />
                <Route path="portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
                <Route path="portfolio/:id" element={<PublicLayout><PortfolioDetail /></PublicLayout>} />
                <Route path="cv" element={<PublicLayout><PublicCV /></PublicLayout>} />
                <Route path="cv/:id" element={<PublicLayout><PublicCV /></PublicLayout>} />
                <Route path="contact" element={<PublicLayout><Contact /></PublicLayout>} />
                <Route path="privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
                <Route path="terms" element={<PublicLayout><Terms /></PublicLayout>} />
                <Route path="refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
                <Route path="eula" element={<PublicLayout><EULA /></PublicLayout>} />
                <Route path="quote/:serviceId" element={<PublicLayout><QuoteRequest /></PublicLayout>} />
                <Route path="book/:id" element={<PublicLayout><ServiceBooking /></PublicLayout>} />
                <Route path="order/:id" element={<PublicLayout><ServiceOrder /></PublicLayout>} />
                <Route path="login" element={<PublicLayout><Login /></PublicLayout>} />
                <Route path="register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />
                
                {/* Replicate Dashboard Routes under /t/:tenantSlug/dashboard */}
                <Route path="dashboard">
                    <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
                    <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
                    <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
                    <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
                    <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
                    <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
                    <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
                    <Route path="requests" element={<ProtectedRoute><ClientMessages /></ProtectedRoute>} />
                    <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
                    <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
                    <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
                    <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
                    <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
                    <Route path="studio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><StudioSettings /></ProtectedRoute>} />
                    <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
                    <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
                    <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
                </Route>
            </Route>

            <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
    );
};

const App: React.FC = () => {
  return (
      <HashRouter>
        <TenantProvider>
             <FaviconInjector />
             <TerminologyProvider>
                <AuthProvider>
                  <NotificationProvider>
                    <Suspense fallback={<PageLoader />}>
                        <AppRoutes />
                    </Suspense>
                  </NotificationProvider>
                </AuthProvider>
             </TerminologyProvider>
        </TenantProvider>
      </HashRouter>
  );
};

export default App;
